import type { MascotConfig } from './mascot-catalog'
import { mixMascotColor } from './mascot-colors'

export function MascotHeadwear({ config }: { config: MascotConfig }) {
  const kind = config.headwear ?? 'none'
  if (kind === 'none') return null
  const color = config.headwearColor ?? '#e6bc65'
  const light = mixMascotColor(color, '#ffffff', 0.55)
  const shade = mixMascotColor(color, '#433047', 0.3)
  return (
    <g data-headwear={kind} stroke={shade} strokeWidth="1.4" strokeLinejoin="round">
      {kind === 'flower' && (
        <g transform="translate(171 59) rotate(15)">
          {[0, 60, 120, 180, 240, 300].map((angle) => (
            <ellipse
              key={angle}
              cx="0"
              cy="-10"
              rx="7"
              ry="12"
              fill={color}
              transform={`rotate(${angle})`}
            />
          ))}
          <circle r="7" fill="#fff0ac" />
          <circle cx="-2" cy="-2" r="2" fill="#fffdf3" stroke="none" />
        </g>
      )}
      {kind === 'crown' && (
        <g>
          <path d="M91 51 86 22l20 14 14-24 15 24 20-14-6 29Z" fill={color} />
          <path d="M93 47h54v9H93Z" fill={light} />
          <path d="m120 31 5 7-5 7-5-7Z" fill="#fff6cd" />
          {[87, 120, 154].map((x, i) => (
            <circle key={x} cx={x} cy={i === 1 ? 12 : 22} r="3" fill={light} />
          ))}
        </g>
      )}
      {kind === 'beret' && (
        <g transform="rotate(-12 120 48)">
          <path d="M78 48c-15-25 66-40 88-14 13 20-49 30-88 14Z" fill={color} />
          <path d="M86 51q34 9 66-6" fill="none" stroke={shade} strokeWidth="7" />
          <path d="m124 23 3-8" strokeWidth="5" strokeLinecap="round" />
          <path d="M88 37q15-12 31-10" fill="none" stroke={light} strokeWidth="3" />
        </g>
      )}
      {kind === 'party' && (
        <g>
          <path d="m93 51 27-46 29 46Z" fill={color} />
          <path d="m102 36 30-11m-36 24 43-15" stroke={light} strokeWidth="5" />
          <circle cx="120" cy="7" r="6" fill={light} />
          <path
            d="M91 53q30 9 60 0"
            stroke={light}
            strokeWidth="7"
            strokeLinecap="round"
          />
        </g>
      )}
      {kind === 'star' && (
        <g transform="translate(168 57) rotate(16)">
          <path d="m0-18 5 12 14 1-11 9 3 13-11-7-12 7 4-13-11-9 14-1Z" fill={color} />
          <path
            d="m-3-8 2 6 6 1"
            stroke={light}
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
        </g>
      )}
    </g>
  )
}

export function MascotPattern({
  pattern,
  color,
  cream,
  face = false,
}: {
  pattern: MascotConfig['pattern']
  color: string
  cream: string
  face?: boolean
}) {
  return (
    <g
      data-pattern={pattern}
      fill={color}
      opacity={pattern === 'rosettes' ? '.92' : '.65'}
    >
      {pattern === 'rosettes' && (
        <>
          {(face
            ? [
                [64, 78, 0.75],
                [87, 60, 0.65],
                [116, 58, 0.55],
                [146, 58, 0.6],
                [173, 79, 0.8],
                [55, 111, 0.55],
                [185, 112, 0.55],
                [70, 144, 0.55],
                [173, 145, 0.5],
              ]
            : [
                [79, 177, 0.65],
                [157, 175, 0.75],
                [73, 199, 0.6],
                [165, 201, 0.65],
                [94, 208, 0.5],
                [147, 211, 0.5],
                [116, 193, 0.45],
                [130, 217, 0.4],
              ]
          ).map(([x, y, scale], index) => (
            <g
              key={index}
              transform={`translate(${x} ${y}) rotate(${index % 2 ? 22 : -18}) scale(${scale})`}
            >
              <path
                d="M-7-8q-9 2-7 10m3 7q6 8 13 3m7-5q7-7-1-14m-5-3q-4-3-8-1"
                fill="none"
                stroke={color}
                strokeWidth="4.5"
                strokeLinecap="round"
              />
              <ellipse cx="1" cy="1" rx="3" ry="2.3" />
            </g>
          ))}
          {face && <path d="m100 76 3-2 1 4-3 1Zm32-5 3 1-1 4-3-1Zm-10 6 3 1-1 3-3-1Z" />}
        </>
      )}
      {pattern === 'spots' &&
        (face ? (
          <>
            <ellipse cx="68" cy="77" rx="12" ry="16" />
            <circle cx="159" cy="65" r="9" />
            <ellipse cx="179" cy="124" rx="9" ry="12" />
          </>
        ) : (
          <>
            <ellipse cx="86" cy="175" rx="8" ry="12" />
            <ellipse cx="152" cy="187" rx="8" ry="11" />
            <circle cx="131" cy="216" r="6" />
          </>
        ))}
      {pattern === 'tuxedo' &&
        (face ? (
          <path
            d="M120 54q-8 31-20 50-23 24-30 49h100q-7-25-30-49-12-19-20-50"
            fill={cream}
            opacity=".9"
          />
        ) : (
          <path d="M103 159q17 12 34 0l8 50q-25 23-49 0Z" fill={cream} />
        ))}
      {pattern === 'blaze' && face && (
        <path d="M115 45h10l10 48-15 33-15-33Z" fill={cream} opacity=".95" />
      )}
      {pattern === 'freckles' &&
        (face ? (
          <>
            {[65, 73, 81, 159, 167, 175].map((x, i) => (
              <circle key={x} cx={x} cy={134 + (i % 3 === 1 ? 4 : 0)} r="2.2" />
            ))}
          </>
        ) : (
          <>
            {[91, 98, 143, 150].map((x, i) => (
              <circle key={x} cx={x} cy={181 + (i % 2) * 7} r="3" />
            ))}
          </>
        ))}
    </g>
  )
}
